import * as React from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Image,
  Spinner,
} from "@fluentui/react-components";
import { useContext, useEffect } from "react";
import { logger } from "../../Logger";
import { Constants } from "../../common/constants";
import { Sites, ErrorMessage, AuthCallResponse } from "../../types/CommonTypes";
import { RequestUtils } from "../../services/RequestUtils";
import { ApiResponse } from "../../types/ApiRequest";
import { TeamsFxContext } from "../Context";
import { TCTeamsFxContext } from "../TCTeamsContext";
import { PolarionTeamsContext } from "../PolarionTeamsContext";
import TeamcenterAuthentication from "./TeamcenterAuthentication";
import PolarionAuthentication from "./PolarionAuthentication";
import { Configuration } from "../../services/configuration";
import { AuthenticationUtils } from "./AuthenticationUtils";

type Props = {
  children: React.ReactNode;
};

// This is the dialog used to login from Teams to Teamcenter
const Authentication: React.FC<Props> = ({ children }) => {
  const teamsContext = useContext(TeamsFxContext);
  const tcTeamsContext = useContext(TCTeamsFxContext);
  const polarionTeamsContext = useContext(PolarionTeamsContext);
  const [error, setError] = React.useState<ErrorMessage | null>(null);
  const [checkingOnboardedStatus, setCheckingOnboardingStatus] =
    React.useState(true);
  const [isOnboarded, setIsOnboarded] = React.useState<boolean>(true);
  const [isSiteOptionsLoaded, setIsSiteOptionsLoaded] = React.useState(false);
  const [siteOptions, setSiteOptions] = React.useState<Sites[]>([]);
  const [selectedSite, setSelectedSite] = React.useState<Sites | null>(
    Configuration.getValueFromLocalStorage(Constants.cacheSelectedSite) || null
  );
  const [onboardedStatusFailed, setOnboardedStatusFailed] =
    React.useState(false);
  const [authenticationType, setAuthenticationType] = React.useState<
    string | null
  >(null);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [open, setOpen] = React.useState(
    Configuration.getValueFromLocalStorage(Constants.cacheSelectedSite) ===
      undefined
  );
  const [i18n, setI18n] = React.useState<{ [key: string]: string }>({});
  const isLoginInProgress = React.useRef(false);

  useEffect(() => {
    if (tcTeamsContext?.teamcenter.isAuthenticated) {
      setIsAuthenticated(true);
      setOpen(false);
    } else if (polarionTeamsContext?.polarion.isAuthenticated) {
      setIsAuthenticated(true);
      setOpen(false);
    } else {
      setIsAuthenticated(false);
      setOpen(true);
    }
  }, [
    tcTeamsContext?.teamcenter.isAuthenticated,
    polarionTeamsContext?.polarion.isAuthenticated,
  ]);

  // get onboarded status from TcTeamsApi
  // this will trigger an https request to TcTeams API on /onboardedstatus
  // the reponse will be 200 if onboarded, otherwise 403
  useEffect(() => {
    async function getOnBoardedStatus() {
      const previousSelectedSite: any = Configuration.getValueFromLocalStorage(
        Constants.cacheSelectedSite
      );
      const response: ApiResponse = await RequestUtils.callTcTeamsApi(
        Constants.opgetOnboardedStatus,
        teamsContext.teamsUserCredential,
        undefined
      );
      if (response.error) {
        setError(response.error);
        setOnboardedStatusFailed(true);
      } else {
        if (response.data && response.data.length > 0) {
          setSiteOptions(response.data);
          if (previousSelectedSite) {
            logger.logInformation(
              "Authentication: Found previous selected site"
            );
            if (
              response.data.find(
                (site: Sites) => site.siteId === previousSelectedSite.siteId
              )
            ) {
              logger.logInformation(
                "Authentication: Setting previous selected site as current selected site"
              );
              setSelectedSite(
                response.data.find(
                  (site: Sites) => site.siteId === previousSelectedSite.siteId
                )
              );
            }
          } else {
            logger.logInformation(
              "Authentication: Setting first site as selected site"
            );
            setSelectedSite(response.data[0]);
          }
          setIsSiteOptionsLoaded(true);
        }
      }
      setCheckingOnboardingStatus(false);
    }

    getOnBoardedStatus();
  }, [setError, teamsContext.teamsUserCredential]);

  // Update loginType when selectedSite changes
  useEffect(() => {
    if (!selectedSite) {
      setAuthenticationType(null);
      return;
    }
    if (
      selectedSite.appName === Constants.Teamcenter ||
      selectedSite.appName === Constants.Designcenter
    ) {
      setAuthenticationType(Constants.Teamcenter);
      logger.logInformation(
        `Authentication: Selected site is related to Teamcenter/Designcenter`
      );
    } else if (selectedSite.appName === Constants.Polarion) {
      setAuthenticationType(Constants.Polarion);
      logger.logInformation(
        `Authentication: Selected site is related to Polarion`
      );
    } else {
      setAuthenticationType(null);
    }
  }, [selectedSite]);

  // Popup the login dialog if the user is not authenticated or the session has expired or the user is not authorized
  useEffect(() => {
    async function startProcessing() {
      if (authenticationType === Constants.Teamcenter) {
        if (
          tcTeamsContext?.failure &&
          tcTeamsContext?.failure.error &&
          tcTeamsContext?.failure.error.statusCode === 403
        ) {
          // if we get a 403, it means the tenant is not onboarded
          logger.logInformation(
            "Teamcenter Authentication: Tenant is not onboarded"
          );
          setIsOnboarded(false);
        } else if (
          tcTeamsContext?.failure &&
          tcTeamsContext?.failure.error &&
          tcTeamsContext?.failure.error.statusCode === 401
        ) {
          if (
            tcTeamsContext.failure.error.message ===
            "Token is invalid or expired."
          ) {
            logger.logInformation("SAM token expired, let user login again.");
            setOpen(true);
            setIsOnboarded(true);
            return;
          }

          const authorizerType: string =
            tcTeamsContext?.configuration?.authorizerType;
          if (
            authorizerType === Constants.SAMAuthentication ||
            authorizerType === Constants.SAM2Authentication
          ) {
            let samTokenString: any = {
              accessToken: "",
              refreshToken: "",
            };

            const retVal: AuthCallResponse = {
              sessionId: undefined,
              userId: undefined,
              userUid: undefined,
              error: undefined,
              userEmailId: undefined,
              userDisplayName: undefined,
              aadObjectId: undefined,
              ssoToken: undefined,
            };
            tcTeamsContext?.failure.setError(undefined);
            logger.logInformation(
              "Teamcenter Authentication: Initiating SAM SSO Login on 401"
            );
            if (isLoginInProgress.current) {
              logger.logInformation(
                "Teamcenter Authentication: SAM SSO Login already in progress, skipping duplicate login attempt"
              );
              return;
            }
            isLoginInProgress.current = true;
            await AuthenticationUtils.initiateSAMEnabledLoginSSO(
              samTokenString,
              teamsContext?.teamsUserCredential,
              retVal,
              ""
            );
            isLoginInProgress.current = false;
            logger.logInformation(
              "Teamcenter Authentication: Completed SAM SSO Login on 401"
            );
            if (retVal.error) {
              logger.logError(
                `Teamcenter Authentication: SAM SSO Login on 401 failed - ${retVal.error}`
              );
              tcTeamsContext?.teamcenter?.setTCisAuthenticated(false);
              tcTeamsContext?.failure.setError(retVal.error);
            } else if (
              retVal &&
              retVal.sessionId &&
              retVal.userId &&
              retVal.userUid
            ) {
              logger.logInformation(
                "Teamcenter Authentication: SAM SSO Login on 401 succeeded"
              );
              tcTeamsContext?.teamcenter?.setTCisAuthenticated(true);
              tcTeamsContext?.teamcenter?.setSession(retVal);
            } else {
              logger.logError(
                "Teamcenter Authentication: SAM SSO Login on 401 failed - Unknown error"
              );
              tcTeamsContext?.teamcenter?.setTCisAuthenticated(false);
            }
          } else {
            logger.logInformation(
              "Teamcenter Authentication: Opening login dialog on 401"
            );
            setOpen(true);
            setIsOnboarded(true); // if we have something else than a 403, the tenant is onboarded
            tcTeamsContext?.teamcenter?.setTCisAuthenticated(false);
          }
        } else if (!tcTeamsContext?.teamcenter.isAuthenticated) {
          logger.logInformation(
            "Teamcenter Authentication: Opening login dialog as user is not authenticated"
          );
          setOpen(true);
          setIsOnboarded(true); // if we have something else than a 403, the tenant is onboarded
        } else {
          logger.logInformation(
            "Teamcenter Authentication: User is authenticated, closing login dialog"
          );
          setOpen(false);
          setIsOnboarded(true); // if we have something else than a 403, the tenant is onboarded
        }
      } else if (authenticationType === Constants.Polarion) {
        if (
          polarionTeamsContext?.failure &&
          polarionTeamsContext?.failure.error &&
          polarionTeamsContext?.failure.error.statusCode === 403
        ) {
          // if we get a 403, it means the tenant is not onboarded
          logger.logInformation(
            "Polarion Authentication: Tenant is not onboarded"
          );
          setIsOnboarded(false);
        } else if (
          polarionTeamsContext?.failure &&
          polarionTeamsContext?.failure.error &&
          polarionTeamsContext?.failure.error.statusCode === 401
        ) {
          const authorizerType: string =
            polarionTeamsContext?.configuration?.authorizerType;
          const configuration: any = Configuration.getValueFromLocalStorage(
            Constants.cachePolarionCode
          );
          if (authorizerType === Constants.SAMAuthentication) {
            // pending on SAM auth flow for Polarion
          } else {
            logger.logInformation(
              "Polarion Authentication: Opening login dialog on 401"
            );
            setOpen(true);
            setIsOnboarded(true); // if we have something else than a 403, the tenant is onboarded
          }
        } else if (!polarionTeamsContext?.polarion.isAuthenticated) {
          setOpen(true);
          setIsOnboarded(true); // if we have something else than a 403, the tenant is onboarded
        } else {
          setOpen(false);
          setIsOnboarded(true); // if we have something else than a 403, the tenant is onboarded
        }
      }
    }
    startProcessing();
  }, [
    teamsContext?.context,
    tcTeamsContext?.failure,
    tcTeamsContext?.teamcenter,
    tcTeamsContext?.teamcenter?.isAuthenticated,
    tcTeamsContext?.configuration?.authorizerType,
    polarionTeamsContext?.failure,
    polarionTeamsContext?.polarion,
    polarionTeamsContext?.polarion?.isAuthenticated,
    polarionTeamsContext?.configuration?.authorizerType,
    teamsContext?.teamsUserCredential,
    authenticationType,
  ]);

  return (
    <>
      <Dialog
        modalType="alert"
        open={open}
        onOpenChange={(_event, data) => setOpen(data.open)}
      >
        <DialogSurface className="login-dialog-surface button">
          <DialogBody>
            <DialogTitle className="text-center">
              {authenticationType === Constants.Teamcenter &&
                i18n.LoginIntoTeamcenter}
              {authenticationType === Constants.Polarion &&
                i18n.LoginIntoPolarion}
            </DialogTitle>
            <DialogContent>
              <div className="text-center">
                <div>
                  <Image src="hello.png" className="image-width" />
                </div>
                {!isSiteOptionsLoaded && (
                  <div className="loading-sites-spinner">
                    <Spinner size="large" label={i18n.LoadingEnvironments} />
                  </div>
                )}
              </div>
              {authenticationType === Constants.Teamcenter && (
                <TeamcenterAuthentication
                  teamsContext={teamsContext}
                  error={error}
                  checkingOnboardedStatus={checkingOnboardedStatus}
                  isOnboarded={isOnboarded}
                  siteOptions={siteOptions}
                  selectedSite={selectedSite}
                  onboardedStatusFailed={onboardedStatusFailed}
                  setSelectedSite={setSelectedSite}
                  isSiteOptionsLoaded={isSiteOptionsLoaded}
                  setIsAuthenticated={setIsAuthenticated}
                  setOpen={setOpen}
                  seti18n={setI18n}
                />
              )}
              {authenticationType === Constants.Polarion && (
                <PolarionAuthentication
                  teamsContext={teamsContext}
                  error={error}
                  checkingOnboardedStatus={checkingOnboardedStatus}
                  isOnboarded={isOnboarded}
                  siteOptions={siteOptions}
                  selectedSite={selectedSite}
                  onboardedStatusFailed={onboardedStatusFailed}
                  setSelectedSite={setSelectedSite}
                  isSiteOptionsLoaded={isSiteOptionsLoaded}
                  setIsAuthenticated={setIsAuthenticated}
                  setOpen={setOpen}
                  seti18n={setI18n}
                />
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {isAuthenticated && <>{children}</>}
    </>
  );
};

export default Authentication;
